import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams, Link } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "../components/ui/select";
import { Loader2, ArrowLeft, RefreshCw } from "lucide-react";

const languageLabel = {
  id: "Bahasa Indonesia",
  en: "English",
  zh: "中文",
};

export default function AdminLeads({ backendUrl }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [limit, setLimit] = useState(() => Number(searchParams.get("limit")) || 50);
  const [language, setLanguage] = useState(() => searchParams.get("language") || "all");

  const fetchLeads = async () => {
    if (!backendUrl) {
      setError("BACKEND_URL belum dikonfigurasi.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (limit) params.limit = limit;
      if (language && language !== "all") params.language = language;

      const token = window.localStorage.getItem("hti_admin_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const res = await axios.get(`${backendUrl}/api/admin/contact-leads`, { params, headers });
      setLeads(res.data || []);
    } catch (err) {
      setError("Gagal memuat data lead. Silakan coba lagi.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const next = {};
    if (limit) next.limit = String(limit);
    if (language) next.language = language;
    setSearchParams(next, { replace: true });
  }, [limit, language, setSearchParams]);

  useEffect(() => {
    fetchLeads();
  }, []);

  const formatDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString("id-ID", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6 text-slate-900 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-6xl flex-col gap-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="px-2" asChild>
              <Link to="/">
                <ArrowLeft className="mr-1 h-4 w-4" />
                Kembali ke Website
              </Link>
            </Button>
            <div>
              <h1 className="text-lg font-semibold tracking-tight">Contact Leads</h1>
              <p className="text-xs text-slate-500">
                Tampilan internal sederhana untuk melihat permintaan konsultasi yang masuk.
              </p>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={fetchLeads} disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-2 h-3.5 w-3.5" />}
            Refresh
          </Button>
        </div>

        <Card className="border-slate-200 bg-white">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Filter</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 text-xs sm:flex-row sm:items-end">
            <div className="w-full max-w-[120px] space-y-1.5">
              <label className="text-[11px] font-medium text-slate-700">Limit</label>
              <Input
                type="number"
                min={1}
                max={200}
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value) || 50)}
                className="h-8 text-xs"
              />
            </div>
            <div className="w-full max-w-[200px] space-y-1.5">
              <label className="text-[11px] font-medium text-slate-700">Bahasa</label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Semua bahasa" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua bahasa</SelectItem>
                  <SelectItem value="id">Bahasa Indonesia</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="zh">中文</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {error}
          </div>
        )}

        <Card className="border-slate-200 bg-white">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Daftar Lead</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table className="min-w-[720px] text-xs">
              <TableHeader>
                <TableRow>
                  <TableHead>Waktu</TableHead>
                  <TableHead>Nama</TableHead>
                  <TableHead>Perusahaan</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone / WA</TableHead>
                  <TableHead>Aplikasi</TableHead>
                  <TableHead>Bahasa</TableHead>
                  <TableHead>Pesan</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={8} className="py-6 text-center text-[11px] text-slate-500">
                      Belum ada data lead yang tersimpan.
                    </TableCell>
                  </TableRow>
                )}
                {leads.map((lead) => (
                  <TableRow key={lead.id} className="align-top">
                    <TableCell className="whitespace-nowrap">{formatDate(lead.createdAt)}</TableCell>
                    <TableCell className="whitespace-nowrap font-medium text-slate-800">{lead.name}</TableCell>
                    <TableCell className="whitespace-nowrap text-slate-700">{lead.company}</TableCell>
                    <TableCell className="whitespace-nowrap text-slate-700">{lead.email}</TableCell>
                    <TableCell className="whitespace-nowrap text-slate-700">{lead.phone || "-"}</TableCell>
                    <TableCell className="whitespace-nowrap text-slate-700">{lead.application || "-"}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      {lead.language ? (
                        <Badge variant="outline" className="border-slate-300 text-[10px]">
                          {languageLabel[lead.language] || lead.language}
                        </Badge>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell className="max-w-xs whitespace-pre-line text-slate-700">
                      {lead.message}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
